import { lazyRoute, PreloadableComponent } from "@/lib/lazyRoute";
// Root-level plain JS so server.js (which cannot import from src/) and this
// table read the same list. See the header of that file.
import { SSR_TOOL_PAGES } from "../../ssrToolPages.js";

/**
 * Every code-split page that fly-social.com server-renders for search engines.
 *
 * These used to be plain `React.lazy` declarations inside App.tsx. They are
 * `lazyRoute` here instead (same dynamic `import()`, same Rollup chunk) so both
 * the server and the client can await the chunk before rendering — see
 * src/lib/lazyRoute.tsx for why `renderToString` otherwise emits the Suspense
 * fallback and ships an empty page to Google.
 *
 * Only pages that are server-rendered belong in this file. /fly-app/* and the
 * token-gated flows stay ordinary `lazy()` in App.tsx: they are not
 * server-rendered, and pulling them in here would put them on the path that
 * the server walks.
 *
 * Careful with the two /tools URLs, which are different things: /tools is the
 * tools DIRECTORY page, an ordinary marketing page. /tools/* are the tools
 * themselves. Most of those are still excluded from SSR on purpose; the named
 * few listed in ssrToolPages.js are the audited exceptions and they appear
 * lower down in this file.
 */

// ── ClinicLeads (healthcare screening product) ───────────────────────────
// The AppSumo and GHL listings both send buyers to /clinicleads, so a cold
// lambda serving it the shell's agency title is a launch-quality problem, not
// a cosmetic one. These three go through lazyRoute for the reason in
// src/lib/lazyRoute.tsx, and App.tsx imports them from here rather than
// declaring its own React.lazy, which is what makes the preload actually
// reach the component the server renders.
export const ClinicLeads = lazyRoute(() => import("@/pages/ClinicLeads"), "ClinicLeads");
export const ClinicLeadsTerms = lazyRoute(() => import("@/pages/clinicleads/ClinicLeadsTerms"), "ClinicLeadsTerms");
export const ClinicLeadsPrivacy = lazyRoute(() => import("@/pages/clinicleads/ClinicLeadsPrivacy"), "ClinicLeadsPrivacy");

// ── Comparison pages ────────────────────────────────────────────────────────
// The three older pages (Yext, Moz Local, BrightLocal) moved to the comparisons below on 15 Oct 26, at the same addresses.

// ── Industry pages ──────────────────────────────────────────────────────────
export const Restaurants = lazyRoute(() => import("@/pages/industries/Restaurants"), "Restaurants");
export const Healthcare = lazyRoute(() => import("@/pages/industries/Healthcare"), "Healthcare");
export const Retail = lazyRoute(() => import("@/pages/industries/Retail"), "Retail");
export const Automotive = lazyRoute(() => import("@/pages/industries/Automotive"), "Automotive");
export const ProfessionalServices = lazyRoute(() => import("@/pages/industries/ProfessionalServices"), "ProfessionalServices");

// ── Docs ────────────────────────────────────────────────────────────────────
export const LeadGenToolsDocs = lazyRoute(() => import("@/pages/docs/LeadGenToolsDocs"), "LeadGenToolsDocs");

// ── Tools directory ─────────────────────────────────────────────────────────
// /tools only, not /tools/*. See the note at the top of this file.
export const ToolsIndex = lazyRoute(() => import("@/pages/ToolsIndex"), "ToolsIndex");

// ── Server-rendered tool pages ──────────────────────────────────────────────
// The audited /tools/* exceptions. Every one of these renders its H1 and its
// full above-the-fold copy from static markup, and neither the page nor
// anything in its import graph touches a browser-only API at module load or
// during render: the Google Maps and canvas work these tools do all lives
// inside useEffect and event handlers, which never run on the server.
//
// These MUST be lazyRoute() and MUST be the same components App.tsx renders.
// A second lazy() declaration of the same page in App.tsx would be a different
// module instance, so preloading this one would leave the one actually being
// rendered unresolved and the server would still serialise an empty div.
//
// The matching URL list lives in ssrToolPages.js, which is what server.js
// reads to decide whether to run the renderer at all.
export const AiSearchVisibility = lazyRoute(() => import("@/pages/tools/ai-search-visibility"), "AiSearchVisibility");
export const BestTimeToPost = lazyRoute(() => import("@/pages/tools/best-time-to-post"), "BestTimeToPost");
export const BrandVoiceAnalyzer = lazyRoute(() => import("@/pages/tools/brand-voice-analyzer"), "BrandVoiceAnalyzer");
export const CaptionGenerator = lazyRoute(() => import("@/pages/tools/caption-generator"), "CaptionGenerator");
export const CitationAuditDeep = lazyRoute(() => import("@/pages/tools/citation-audit-deep"), "CitationAuditDeep");
export const CitationChecker = lazyRoute(() => import("@/pages/tools/CitationChecker"), "CitationChecker");
export const CompetitorAnalysisNearby = lazyRoute(() => import("@/pages/tools/competitor-analysis-nearby"), "CompetitorAnalysisNearby");
export const CompetitorSocialAudit = lazyRoute(() => import("@/pages/tools/competitor-social-audit"), "CompetitorSocialAudit");

// ── Blog index + articles ───────────────────────────────────────────────────
export const BlogIndex = lazyRoute(() => import("@/pages/blogs/BlogIndex"), "BlogIndex");
export const GoogleBusinessProfileSuspension = lazyRoute(() => import("@/pages/blogs/GoogleBusinessProfileSuspension"), "GoogleBusinessProfileSuspension");
export const GMBSuspensionRecoveryTime = lazyRoute(() => import("@/pages/blogs/GMBSuspensionRecoveryTime"), "GMBSuspensionRecoveryTime");
export const PostingFakeReviewsKeywordStuffing = lazyRoute(() => import("@/pages/blogs/PostingFakeReviewsKeywordStuffing"), "PostingFakeReviewsKeywordStuffing");
export const UpdatingBusinessInfoGMBSuspension = lazyRoute(() => import("@/pages/blogs/UpdatingBusinessInfoGMBSuspension"), "UpdatingBusinessInfoGMBSuspension");
export const MultipleProfilesBackfire = lazyRoute(() => import("@/pages/blogs/MultipleProfilesBackfire"), "MultipleProfilesBackfire");
export const GMBReinstatementRequest = lazyRoute(() => import("@/pages/blogs/GMBReinstatementRequest"), "GMBReinstatementRequest");
export const SampleAppealTemplate = lazyRoute(() => import("@/pages/blogs/SampleAppealTemplate"), "SampleAppealTemplate");
export const GMBReinstatementDenied = lazyRoute(() => import("@/pages/blogs/GMBReinstatementDenied"), "GMBReinstatementDenied");
export const GMBAccountSuspensionPrevention = lazyRoute(() => import("@/pages/blogs/GMBAccountSuspensionPrevention"), "GMBAccountSuspensionPrevention");
export const GMBReinstatementAppealEssentials = lazyRoute(() => import("@/pages/blogs/GMBReinstatementAppealEssentials"), "GMBReinstatementAppealEssentials");
export const GMBReinstatementDelays = lazyRoute(() => import("@/pages/blogs/GMBReinstatementDelays"), "GMBReinstatementDelays");
export const ClinicGrowthDigitalStrategies = lazyRoute(() => import("@/pages/blogs/ClinicGrowthDigitalStrategies"), "ClinicGrowthDigitalStrategies");
export const LocalSEOChecklistClinic = lazyRoute(() => import("@/pages/blogs/LocalSEOChecklistClinic"), "LocalSEOChecklistClinic");
export const AutoReviewRepliesTool = lazyRoute(() => import("@/pages/blogs/AutoReviewRepliesTool"), "AutoReviewRepliesTool");
export const GetPatientsGBPRoadmap = lazyRoute(() => import("@/pages/blogs/GetPatientsGBPRoadmap"), "GetPatientsGBPRoadmap");
export const GMBMaintenancePlan = lazyRoute(() => import("@/pages/blogs/GMBMaintenancePlan"), "GMBMaintenancePlan");
export const GMBProfileAudits = lazyRoute(() => import("@/pages/blogs/GMBProfileAudits"), "GMBProfileAudits");
export const AgencySuspensionProofGBP = lazyRoute(() => import("@/pages/blogs/AgencySuspensionProofGBP"), "AgencySuspensionProofGBP");
export const AgencyManpowerFlyAgent = lazyRoute(() => import("@/pages/blogs/AgencyManpowerFlyAgent"), "AgencyManpowerFlyAgent");
export const GbpAuditImproveLocalRankings = lazyRoute(() => import("@/pages/blogs/GbpAuditImproveLocalRankings"), "GbpAuditImproveLocalRankings");
export const LocalRankChecker = lazyRoute(() => import("@/pages/blogs/LocalRankChecker"), "LocalRankChecker");
export const AgenciesGbpAuditLeads = lazyRoute(() => import("@/pages/blogs/AgenciesGbpAuditLeads"), "AgenciesGbpAuditLeads");
export const FlySocialToolsGuide = lazyRoute(() => import("@/pages/blogs/FlySocialToolsGuide"), "FlySocialToolsGuide");
export const GenerateLocalSeoLeadsAuditTools = lazyRoute(() => import("@/pages/blogs/GenerateLocalSeoLeadsAuditTools"), "GenerateLocalSeoLeadsAuditTools");
// The /blogs/:slug catch-all. See the PREFIX_ROUTES note below.
export const DatabaseBlogPost = lazyRoute(() => import("@/pages/blogs/DatabaseBlogPost"), "DatabaseBlogPost");

// ── How-to guides (WO-I2, Anand, 14 Oct 26) ─────────────────────────────────
// The hub and one page component for the eleven guides; each guide is a small data module under
// src/pages/guides/content. The first eight addresses are fixed: the agent landing links to them.
export const GuidesHub = lazyRoute(() => import("@/pages/guides/GuidesHub"), "GuidesHub");
export const GuidePage = lazyRoute(() => import("@/pages/guides/GuidePage"), "GuidePage");

// ── Comparisons (Anand, 15 Oct 26: "yes start the comparison pages") ───────
// The hub, the round-up of AI agents for Google Business Profile and one component for the "Fly vs" pages; each tool
// is one profile in src/pages/compare/content/tools.ts. The three older /comparisons/fly-vs-* pages stay as they are.
export const CompareHub = lazyRoute(() => import("@/pages/compare/CompareHub"), "CompareHub");
export const BestAgentsPage = lazyRoute(() => import("@/pages/compare/BestAgentsPage"), "BestAgentsPage");
// The round-up for a business in India, rupee and dollar prices (Shardul, 29 Oct 26: "go ahead" on the answer pages).
export const BestIndiaPage = lazyRoute(() => import("@/pages/compare/BestIndiaPage"), "BestIndiaPage");
// The round-up for "automated Google Business Profile manager" (Shardul, 03 Nov 26).
export const BestAutomatedPage = lazyRoute(() => import("@/pages/compare/BestAutomatedPage"), "BestAutomatedPage");
// The round-up for "best AI tools for Google Business Profile" (Shardul, 04 Nov 26).
export const BestAiToolsPage = lazyRoute(() => import("@/pages/compare/BestAiToolsPage"), "BestAiToolsPage");
export const VsComparePage = lazyRoute(() => import("@/pages/compare/VsComparePage"), "VsComparePage");

// ── Fly Agent landing pages ─────────────────────────────────────────────────
export const FlyAgentForClaude = lazyRoute(() => import("@/pages/mcp/FlyAgentForClaude"), "FlyAgentForClaude");
export const AgentPage = lazyRoute(() => import("@/pages/mcp/AgentPage"), "AgentPage");
export const AgenciesPage = lazyRoute(() => import("@/pages/mcp/AgenciesPage"), "AgenciesPage");
export const MultiLocationPage = lazyRoute(() => import("@/pages/mcp/MultiLocationPage"), "MultiLocationPage");
export const FreelancersPage = lazyRoute(() => import("@/pages/mcp/FreelancersPage"), "FreelancersPage");
export const LocalBusinessPage = lazyRoute(() => import("@/pages/mcp/LocalBusinessPage"), "LocalBusinessPage");

// MCP guides barrel — per-name so marketing traffic doesn't ship every guide.
export const ChatGPTGuide = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.ChatGPTGuide })), "ChatGPTGuide");
export const ClaudeGuide = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.ClaudeGuide })), "ClaudeGuide");
export const GeminiGuide = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.GeminiGuide })), "GeminiGuide");
export const CoworkPluginGuide = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.CoworkPluginGuide })), "CoworkPluginGuide");
export const AgencyWorkflowsGuide = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.AgencyWorkflowsGuide })), "AgencyWorkflowsGuide");
export const GuidesLandingPage = lazyRoute(() => import("@/pages/mcp/guides").then((m) => ({ default: m.GuidesLandingPage })), "GuidesLandingPage");

// ── Agent Lite landing pages (public, indexed) ──────────────────────────────
export const AutoReviewResponderPage = lazyRoute(() => import("@/pages/agent/AutoReviewResponderPage"), "AutoReviewResponderPage");
export const NegativeReviewAlertPage = lazyRoute(() => import("@/pages/agent/NegativeReviewAlertPage"), "NegativeReviewAlertPage");
export const ProfileProtectionPage = lazyRoute(() => import("@/pages/agent/ProfileProtectionPage"), "ProfileProtectionPage");
export const ReviewGeneratorPage = lazyRoute(() => import("@/pages/agent/ReviewGeneratorPage"), "ReviewGeneratorPage");
export const RestaurantsCategoryLanding = lazyRoute(() => import("@/pages/agent/categories").then((m) => ({ default: m.RestaurantsLanding })), "RestaurantsCategoryLanding");
export const HealthcareCategoryLanding = lazyRoute(() => import("@/pages/agent/categories").then((m) => ({ default: m.HealthcareLanding })), "HealthcareCategoryLanding");
export const BeautyCategoryLanding = lazyRoute(() => import("@/pages/agent/categories").then((m) => ({ default: m.BeautyLanding })), "BeautyCategoryLanding");
export const HomeServicesCategoryLanding = lazyRoute(() => import("@/pages/agent/categories").then((m) => ({ default: m.HomeServicesLanding })), "HomeServicesCategoryLanding");
export const AutomotiveCategoryLanding = lazyRoute(() => import("@/pages/agent/categories").then((m) => ({ default: m.AutomotiveLanding })), "AutomotiveCategoryLanding");

type AnyPreloadable = PreloadableComponent<never> | PreloadableComponent<object>;

/**
 * URL path -> the chunk that path renders.
 *
 * Kept as a flat table rather than derived from App.tsx's <Route> tree because
 * the preload has to happen BEFORE React Router runs. It only needs to cover
 * server-rendered marketing URLs; anything missing here simply falls back to
 * the old suspend-and-swap behaviour, it does not break.
 */
const EXACT_ROUTES: Record<string, AnyPreloadable> = {
  "/clinicleads": ClinicLeads,
  "/clinicleads/terms": ClinicLeadsTerms,
  "/clinicleads/privacy": ClinicLeadsPrivacy,

  "/comparisons": CompareHub,
  "/comparisons/best-ai-agent-for-google-business-profile": BestAgentsPage,
  "/comparisons/best-ai-tools-for-google-business-profile": BestAiToolsPage,
  "/comparisons/best-automated-google-business-profile-manager": BestAutomatedPage,
  "/comparisons/best-google-business-profile-tools-india": BestIndiaPage,
  "/comparisons/fly-vs-semrush-gbp-ai-agent": VsComparePage,
  "/comparisons/fly-vs-merchynt-paige": VsComparePage,
  "/comparisons/fly-vs-localo": VsComparePage,
  "/comparisons/fly-vs-birdeye": VsComparePage,
  "/comparisons/fly-vs-yext": VsComparePage,
  "/comparisons/fly-vs-moz-local": VsComparePage,
  "/comparisons/fly-vs-brightlocal": VsComparePage,
  "/comparisons/fly-vs-google-business-profile-agency": VsComparePage,

  "/industries/restaurants": Restaurants,
  "/industries/healthcare": Healthcare,
  "/industries/retail": Retail,
  "/industries/automotive": Automotive,
  "/industries/professional-services": ProfessionalServices,

  "/docs/lead-gen-tools": LeadGenToolsDocs,

  "/tools": ToolsIndex,

  // /tools/* exceptions. Keep in step with ssrToolPages.js; the check below
  // this table shouts if the two ever drift.
  "/tools/ai-search-visibility": AiSearchVisibility,
  "/tools/best-time-to-post": BestTimeToPost,
  "/tools/brand-voice-analyzer": BrandVoiceAnalyzer,
  "/tools/caption-generator": CaptionGenerator,
  "/tools/citation-audit-deep": CitationAuditDeep,
  "/tools/citation-checker": CitationChecker,
  "/tools/competitor-analysis-nearby": CompetitorAnalysisNearby,
  "/tools/competitor-social-audit": CompetitorSocialAudit,

  "/blogs": BlogIndex,
  "/blogs/generate-local-seo-leads-free-audit-tools": GenerateLocalSeoLeadsAuditTools,
  "/blogs/google-business-profile-suspension": GoogleBusinessProfileSuspension,
  "/blogs/gmb-suspension-recovery-time": GMBSuspensionRecoveryTime,
  "/blogs/posting-fake-reviews-keyword-stuffing": PostingFakeReviewsKeywordStuffing,
  "/blogs/updating-business-info-gmb-suspension": UpdatingBusinessInfoGMBSuspension,
  "/blogs/multiple-profiles-backfire": MultipleProfilesBackfire,
  "/blogs/gmb-reinstatement-request": GMBReinstatementRequest,
  "/blogs/google-business-profile-audit-improve-local-rankings": GbpAuditImproveLocalRankings,
  "/blogs/local-rank-checker": LocalRankChecker,
  "/blogs/agencies-more-local-seo-clients-gbp-audits": AgenciesGbpAuditLeads,
  "/blogs/sample-appeal-template": SampleAppealTemplate,
  "/blogs/gmb-reinstatement-denied": GMBReinstatementDenied,
  "/blogs/gmb-account-suspension-prevention": GMBAccountSuspensionPrevention,
  "/blogs/gmb-reinstatement-appeal-essentials": GMBReinstatementAppealEssentials,
  "/blogs/gmb-reinstatement-delays-tips-workarounds": GMBReinstatementDelays,
  "/blogs/clinic-growth-digital-strategies": ClinicGrowthDigitalStrategies,
  "/blogs/local-seo-checklist-clinic": LocalSEOChecklistClinic,
  "/blogs/auto-review-replies-tool": AutoReviewRepliesTool,
  "/blogs/get-patients-gbp-roadmap": GetPatientsGBPRoadmap,
  "/blogs/gmb-maintenance-plan": GMBMaintenancePlan,
  "/blogs/gmb-profile-audits": GMBProfileAudits,
  "/blogs/agency-suspension-proof-gbp": AgencySuspensionProofGBP,
  "/blogs/agency-manpower-fly-agent": AgencyManpowerFlyAgent,
  "/blogs/fly-social-tools-complete-guide": FlySocialToolsGuide,

  "/guides": GuidesHub,
  "/guides/add-booking-link-google-business-profile": GuidePage,
  "/guides/set-holiday-hours-google-business-profile": GuidePage,
  "/guides/add-services-google-business-profile": GuidePage,
  "/guides/post-offer-google-business-profile": GuidePage,
  "/guides/reply-to-1-star-google-review": GuidePage,
  "/guides/check-local-rank-google-maps": GuidePage,
  "/guides/google-review-link-and-qr-code": GuidePage,
  "/guides/report-google-review": GuidePage,
  "/guides/get-google-business-profile-ready-for-diwali": GuidePage,
  "/guides/automate-google-business-profile": GuidePage,
  "/guides/google-business-profile-without-website": GuidePage,

  "/fly-agent": FlyAgentForClaude,
  "/fly-agent/agencies": AgenciesPage,
  "/fly-agent/multi-location": MultiLocationPage,
  "/fly-agent/freelancers": FreelancersPage,
  "/fly-agent/local-business": LocalBusinessPage,
  "/fly-agent/guides": GuidesLandingPage,
  "/fly-agent/guides/chatgpt": ChatGPTGuide,
  "/fly-agent/guides/claude": ClaudeGuide,
  "/fly-agent/guides/gemini": GeminiGuide,
  "/fly-agent/guides/cowork-plugin": CoworkPluginGuide,
  "/fly-agent/guides/agency-workflows": AgencyWorkflowsGuide,

  "/agent/lite/auto-review-responder": AutoReviewResponderPage,
  "/agent/lite/negative-review-alert": NegativeReviewAlertPage,
  "/agent/lite/profile-protection": ProfileProtectionPage,
  "/agent/lite/review-generator": ReviewGeneratorPage,
  "/agent/lite/restaurants": RestaurantsCategoryLanding,
  "/agent/lite/healthcare": HealthcareCategoryLanding,
  "/agent/lite/beauty": BeautyCategoryLanding,
  "/agent/lite/home-services": HomeServicesCategoryLanding,
  "/agent/lite/automotive": AutomotiveCategoryLanding,
};

/**
 * Prefix rules for the one parameterised marketing route we render:
 * /fly-agent/:agentId. Consulted only when no exact entry matched, so the
 * named /fly-agent/* pages above still win.
 */
const PREFIX_ROUTES: Array<[string, AnyPreloadable]> = [
  ["/fly-agent/", AgentPage],
  // /blogs/:slug, the database-backed article page. routeChunkFor checks
  // EXACT_ROUTES first, so all 27 hardcoded posts listed above still resolve to
  // their own chunk; this only fires for a slug none of them owns.
  //
  // Preloading it gets the article SHELL server-rendered instead of an empty
  // Suspense fallback. It does NOT get the article itself into the server HTML:
  // renderToString is synchronous and cannot await the query, so a database
  // article's body, title and JSON-LD are all written by the client. Giving
  // these posts real server-rendered HTML means teaching server.js to fetch the
  // row up front, the way it already does for microsite blog_detail.
  ["/blogs/", DatabaseBlogPost],
];

// A tool path that server.js will server-render but that has no entry above
// would render the empty Suspense fallback, which is exactly the "page reaches
// Google with no H1" bug this whole mechanism exists to prevent. Say so loudly
// rather than shipping a silently blank page.
for (const path of SSR_TOOL_PAGES) {
  if (!EXACT_ROUTES[path]) {
    console.error(
      `[marketingRoutes] ${path} is opted into SSR by ssrToolPages.js but has no chunk in this table, so the server will render an empty page for it. Add a lazyRoute entry.`,
    );
  }
}

/** Strips the query string and any trailing slash (except for "/"). */
function normalisePath(url: string): string {
  const path = url.split("?")[0].split("#")[0];
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
}

/** The chunk this URL needs, or null when the URL has no split page. */
export function routeChunkFor(url: string): AnyPreloadable | null {
  const path = normalisePath(url);
  const exact = EXACT_ROUTES[path];
  if (exact) return exact;
  for (const [prefix, component] of PREFIX_ROUTES) {
    if (path.startsWith(prefix)) return component;
  }
  return null;
}

/**
 * Loads the chunk this URL renders, so the next render can be synchronous.
 *
 * Never rejects and never hangs: a failed import or one that outruns
 * `timeoutMs` resolves anyway, and the render then falls back to the old
 * suspend-and-show-the-fallback behaviour. A slow chunk must not be able to
 * hold a request open.
 */
export function preloadRouteFor(url: string, timeoutMs = 3000): Promise<void> {
  const component = routeChunkFor(url);
  if (!component) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      if (!settled) {
        console.warn(`[preloadRouteFor] ${url} did not load within ${timeoutMs}ms, falling back to Suspense`);
      }
      done();
    }, timeoutMs);
    // Don't hold the Node event loop open for a timer nobody is waiting on.
    (timer as unknown as { unref?: () => void }).unref?.();

    component.preload().then(done, (err) => {
      console.error(`[preloadRouteFor] ${url} failed to load, falling back to Suspense:`, err);
      done();
    });
  });
}
